<template>
  <a-spin :spinning="loading">
    <template #indicator>
      <span></span>
    </template>
    <template #tip>
      <div class="flex w-full items-center justify-center">
        <div
          class="team-loader bg-white rounded-xl shadow-lg p-8 max-w-md w-full flex flex-col items-center justify-center transition-all duration-500"
          :class="{ 'opacity-0 scale-95': isLoadingComplete }"
        >
          <!-- 团队图标 -->
          <div class="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-lg mb-6">
            {{ currentTeam.name?.[0] || '' }}
          </div>
          <!-- 主标题 -->
          <h3 class="text-[clamp(1.1rem,2vw,1.3rem)] font-semibold text-gray-800 mb-2 text-center">正在切换团队</h3>
          <!-- 团队名称显示 -->
          <div class="text-xl text-gray-600 mb-8 team-name-fade transition-opacity duration-500">
            <span :style="{ opacity: teamNameOpacity }">{{ currentTeam.name }}</span>
          </div>
          <!-- 加载指示器 -->
          <div class="flex items-center justify-center mb-6">
            <div class="dot-pulse"></div>
            <div class="dot-pulse"></div>
            <div class="dot-pulse"></div>
          </div>
          <!-- 状态提示 -->
          <p class="text-sm text-gray-500 text-center">正在加载团队数据和权限配置...</p>
        </div>
      </div>
    </template>
    <div class="flex w-screen h-screen relative overflow-hidden">
      <canvas ref="bgCanvas" class="absolute inset-0 w-full h-full pointer-events-none z-0"></canvas>
      <div
        class="flex flex-col shadow-sm bg-[#FBFCFD] border-r border-[#FFFFFF] flex-shrink-0 relative z-30"
        :class="showMini ? 'shrink-element' : 'shrink-element-big'"
      >
        <div class="flex items-center justify-center pb-7 pt-10">
          <img src="@/assets/images/logo.png" alt="logo" class="w-8 h-8 pointer" />
          <span class="text-2xl font-bold ml-2" v-show="!showMini">SkyBound</span>
        </div>
        <div
          class="flex flex-col gap-2 px-4 h-[calc(100vh-12rem)] overflow-auto relative"
          :class="showMini ? 'items-center h-[calc(100vh-7rem)]' : ''"
        >
          <a-menu
            id="menu"
            v-model:selectedKeys="activeKey"
            mode="inline"
            :inline-collapsed="showMini"
            style="border: none; background: inherit"
          >
            <a-menu-item
              class="flex items-center pointer flex-shrink-0 hover_text relative"
              :class="{ line_before: item.key === activeKey[0] && showMini }"
              v-for="item in menuItems"
              :key="item.key"
              @click="handleClick(item)"
            >
              <template #icon>
                <custom-icon
                  :name="item.icon"
                  width="1.2rem"
                  height="1.2rem"
                  :color="item.key === activeKey[0] ? '#7C3AED' : '#738192'"
                />
              </template>
              <template #default>
                <div class="flex items-center justify-between">
                  <span
                    class="text-base font-medium sidebar_text flex-shrink-0"
                    :class="item.key === activeKey[0] ? 'text-[#7C3AED]' : 'text-[#738192]'"
                    >{{ item.name }}</span
                  >
                  <div
                    class="w-1 h-1 rounded-full bg-[#7C3AED] flex-shrink-0"
                    v-if="item.key === activeKey[0] && !showMini"
                  ></div>
                </div>
              </template>
            </a-menu-item>
          </a-menu>
          <div
            class="flex items-center justify-center absolute bottom-4 left-1/2 transform -translate-x-1/2 w-8 h-8 rounded-full bg-white pointer"
            style="box-shadow: 0px 0px 45.71px 10px rgba(0, 0, 0, 0.03)"
            @click="showMini = !showMini"
          >
            <custom-icon name="back" width="1rem" height="1rem" color="#80838A" :class="showMini ? 'rotate-180' : ''" />
          </div>
        </div>
        <!-- 团队切换列表 -->
        <div
          class="flex flex-col bg-white rounded-2xl absolute z-50 gap-2 p-4 max-h-[16rem] overflow-y-auto border border-gray-100 shadow-xl transition-all duration-300 w-56"
          :class="showMini ? 'left-[90px] bottom-8' : 'bottom-28 left-4 right-4'"
          v-if="showTeam"
        >
          <div
            class="flex items-center justify-between px-4 py-2 rounded-md pointer hover:bg-gray-50 transition-colors"
            :class="{ team_active: item.value === currentTeam.id, team_disabled: item.disabled }"
            v-for="item in teamOptions"
            :key="item.value"
            @click="handleChooseTeam(item)"
          >
            <div class="flex items-center gap-2">
              <div
                class="flex items-center justify-center w-6 h-6 text-xs text-white rounded-md flex-shrink-0"
                style="background: linear-gradient(132deg, #8e4dff -2%, #913bd3 109%, rgba(124, 58, 237, 0) 109%)"
              >
                {{ item.label?.charAt(0) || '' }}
              </div>
              <span
                class="text-sm truncate max-w-[7.5rem]"
                :class="item.value === currentTeam.id ? 'text-[#7C3AED]' : 'text-[#1E293B]'"
                >{{ item.label || '' }}</span
              >
            </div>
            <div
              class="w-1.5 h-1.5 rounded-full bg-[#7C3AED] flex-shrink-0 shadow-[0_0_4px_rgba(124,58,237,0.5)]"
              v-if="item.value === currentTeam.id"
            ></div>
          </div>
        </div>
        <!-- 底部团队按钮 -->
        <div
          class="flex items-center justify-between team_item rounded-2xl pointer transition-all duration-300 hover:bg-white/60"
          :class="[showTeam ? 'active' : '', showMini ? 'mx-2 px-2 justify-center h-12' : 'mx-4 px-3']"
          @click="handleTeamClick"
        >
          <div class="flex items-center" :class="showMini ? 'justify-center w-full gap-0' : 'gap-3'">
            <div
              class="flex items-center justify-center w-8 h-8 text-xs text-white rounded-md flex-shrink-0 shadow-md transition-transform duration-300 group-hover:scale-105"
              style="background: linear-gradient(132deg, #8e4dff -2%, #913bd3 109%, rgba(124, 58, 237, 0) 109%)"
            >
              {{ userInfo?.organization?.name?.charAt(0) || '' }}
            </div>
            <div
              class="flex flex-col overflow-hidden transition-all duration-300"
              :style="{ width: showMini ? '0' : 'auto', opacity: showMini ? '0' : '1' }"
            >
              <span class="text-xs text-[#9AA0AC] whitespace-nowrap scale-90 origin-left">当前团队</span>
              <span class="text-sm text-[#1E293B] font-medium truncate w-24">{{
                userInfo?.organization?.name || ''
              }}</span>
            </div>
          </div>
          <custom-icon
            name="back"
            width="1rem"
            height="1rem"
            color="#80838A"
            :class="showTeam ? 'rotate-90' : '-rotate-90'"
            v-show="!showMini"
          />
        </div>
      </div>
      <div
        class="flex flex-col h-full relative z-10"
        :style="{ width: showMini ? 'calc(100vw - 6rem)' : 'calc(100vw - 14rem)' }"
      >
        <div
          class="flex items-center pt-4 justify-between h-24 px-8 flex-shrink-0 relative backdrop-blur-sm z-20 transition-all duration-300"
        >
          <div class="flex flex-col">
            <span class="text-lg font-semibold">{{ currentRoute?.meta.title }}</span>
            <span class="text-sm text-[#64758B]">{{ currentRoute?.meta.subTitle }}</span>
          </div>
          <div class="flex items-center gap-3">
            <theme-switch />
            <a-popover placement="bottom">
              <template #content>
                <div class="w-24 h-auto">
                  <p class="w-full py-1 text-center text-sm cursor-pointer hover:bg-[#F9FAFB]" @click="onLogOut">
                    退出登录
                  </p>
                </div>
              </template>
              <div class="flex items-center gap-2 pointer">
                <a-avatar :src="userInfo?.avatar" class="w-9 h-9" v-if="userInfo?.avatar" />
                <img src="@/assets/images/default_avatar.png" class="w-9 h-9" v-else />
                <span class="font-medium text-base">{{ userInfo?.nickName || '' }}</span>
              </div>
            </a-popover>
          </div>
        </div>
        <div class="body_bg" :style="{ width: showMini ? 'calc(100vw - 6rem)' : 'calc(100vw - 14rem)' }">
          <router-view v-slot="{ Component }">
            <transition name="fade-slide" mode="out-in">
              <div :key="route.name" class="w-full h-full">
                <component :is="Component" />
              </div>
            </transition>
          </router-view>
        </div>
      </div>
    </div>
  </a-spin>
</template>
<script lang="ts" setup>
import { notification } from 'ant-design-vue';
import _ from 'lodash';
import dayjs from 'dayjs';

const { getOrganizationList, switchOrganization, getPermissionById } = team;
const { getUserDetail } = api;
const isLoadingComplete = ref(false);
const teamNameOpacity = ref(0);
const menuItems = ref();
const loading = ref(false);
const route = useRoute();
const activeKey: any = ref([]);
const menuStore = useMenuStore();
const userInfoStore = useUserInfoStore();
const { getTabsByPage } = utils;
const userInfo = ref();
const currentRoute = ref();
const teamOptions: any = ref([]);
const showMini = ref(false);
const currentTeam: any = ref({});
const router = useRouter();
const showTeam = ref(false);
const bgCanvas = ref<HTMLCanvasElement | null>(null);
let animationFrameId: number;

const initCanvas = () => {
  const canvas = bgCanvas.value;
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  let width = window.innerWidth;
  let height = window.innerHeight;

  const resize = () => {
    width = window.innerWidth;
    height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;
  };

  window.addEventListener('resize', resize);
  resize();

  // 粒子配置
  const particles: Particle[] = [];
  const particleCount = 60; // 粒子数量
  const connectionDistance = 150; // 连线距离

  const mouse = { x: -1000, y: -1000 };

  class Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    color: string;

    constructor() {
      this.x = Math.random() * width;
      this.y = Math.random() * height;
      this.vx = (Math.random() - 0.5) * 0.5; // 速度
      this.vy = (Math.random() - 0.5) * 0.5;
      this.size = Math.random() * 2 + 1; // 大小
      this.color = `rgba(22, 93, 255, ${Math.random() * 0.3 + 0.1})`; // 蓝色系
    }

    update() {
      this.x += this.vx;
      this.y += this.vy;

      // 边界检查
      if (this.x < 0 || this.x > width) this.vx *= -1;
      if (this.y < 0 || this.y > height) this.vy *= -1;
    }

    draw() {
      if (!ctx) return;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = this.color;
      ctx.fill();
    }
  }

  // 初始化粒子
  for (let i = 0; i < particleCount; i++) {
    particles.push(new Particle());
  }

  let time = 0;
  const animate = () => {
    // 动态渐变背景
    time += 0.002;
    const gradient = ctx.createLinearGradient(0, 0, width, height);

    // 基础色为白色，辅色为极淡的 #7C3AED (紫色系)
    // #7C3AED 的 HSL 大约是 263, 82%, 60%
    // 我们使用更高的亮度 (95%+) 来确保它看起来很淡
    const h1 = 263 + Math.sin(time) * 5; // 在紫色附近轻微波动

    gradient.addColorStop(0, 'hsla(263, 20%, 97%, 1)'); // 起点微带紫色
    gradient.addColorStop(0.7, 'hsla(263, 10%, 99%, 1)'); // 过渡到近白
    gradient.addColorStop(1, `hsla(${h1}, 40%, 96%, 1)`); // 终点为极淡的紫色

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // 添加局部高亮光斑
    const cx = width * 0.8;
    const cy = height * 0.2;
    const radialGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, width * 0.6);
    radialGradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
    radialGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = radialGradient;
    ctx.fillRect(0, 0, width, height);

    // 第二个动态光斑
    const cx2 = width * 0.2 + Math.sin(time * 0.5) * 100;
    const cy2 = height * 0.8 + Math.cos(time * 0.5) * 100;
    const radialGradient2 = ctx.createRadialGradient(cx2, cy2, 0, cx2, cy2, width * 0.5);
    radialGradient2.addColorStop(0, 'rgba(124, 58, 237, 0.03)');
    radialGradient2.addColorStop(1, 'rgba(124, 58, 237, 0)');

    ctx.fillStyle = radialGradient2;
    ctx.fillRect(0, 0, width, height);

    // 右侧额外光斑效果
    const cx3 = width * 0.7 + Math.cos(time * 0.3) * 150;
    const cy3 = height * 0.5 + Math.sin(time * 0.4) * 100;
    const radialGradient3 = ctx.createRadialGradient(cx3, cy3, 0, cx3, cy3, width * 0.4);
    radialGradient3.addColorStop(0, 'rgba(124, 58, 237, 0.1)'); // 极淡的紫色光斑
    radialGradient3.addColorStop(1, 'rgba(124, 58, 237, 0)');

    ctx.fillStyle = radialGradient3;
    ctx.fillRect(0, 0, width, height);

    // 鼠标交互高亮
    if (mouse.x > 0) {
      const mouseGradient = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 300);
      mouseGradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
      mouseGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = mouseGradient;
      ctx.fillRect(0, 0, width, height);
    }

    // 绘制连接线
    for (let i = 0; i < particles.length; i++) {
      const p1 = particles[i];
      p1.update();
      p1.draw();

      // 鼠标互动：连线与吸引
      const mdx = mouse.x - p1.x;
      const mdy = mouse.y - p1.y;

      for (let j = i + 1; j < particles.length; j++) {
        const p2 = particles[j];
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < connectionDistance) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(22, 93, 255, ${0.1 * (1 - distance / connectionDistance)})`;
          ctx.lineWidth = 1;
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }
      }
    }

    animationFrameId = requestAnimationFrame(animate);
  };

  animate();

  // 清理
  onUnmounted(() => {
    window.removeEventListener('resize', resize);
    cancelAnimationFrame(animationFrameId);
  });
};

watch(
  route,
  (newRoute) => {
    if (newRoute) {
      currentRoute.value = newRoute;
      if (!localStorage.getItem('tokenValue')) {
        localStorage.clear();
        router.replace({ path: '/' });
        return;
      }
      if (newRoute.name === 'login') return;
      activeKey.value = [newRoute.meta.key as string];
      const tabs = getTabsByPage(newRoute.meta.key as string);
      const pageType = newRoute.params?.type as string;
      if (pageType) {
        const hasPermission = tabs.some((item) => item.key === pageType);
        if (!hasPermission) {
          activeKey.value = [];
          router.push({ path: '/404' });
        }
      } else {
        const hasPermission = menuItems.value.some((item) => newRoute.fullPath.includes(item.key));
        if (!hasPermission) {
          activeKey.value = [];
          router.push({ path: '/404' });
        }
      }
    }
  },
  {
    deep: true,
  },
);

const onLogOut = () => {
  localStorage.clear();
  sessionStorage.clear();
  router.replace({ path: '/' });
};

onMounted(() => {
  if (!localStorage.getItem('tokenValue')) {
    localStorage.clear();
    router.replace({ path: '/' });
    return;
  }
  currentRoute.value = route;
  activeKey.value = [route.meta.key as string];
  getTeamOptions();
  initCanvas();
});

const getTeamOptions = async () => {
  try {
    const res: any = await getOrganizationList({ id: userInfo.value.id });
    teamOptions.value = res.map((item) => ({
      label: item.name,
      value: item.id,
      disabled: item.status === 'DISABLE',
      createTime: item.createTime,
      account: item.account,
      spendAccount: item.spendAccount,
    }));
    const team: any = teamOptions.value.find((item: any) => item.value === userInfo.value.latestLoginOrganizationId);
    currentTeam.value = {
      id: team.value,
      name: team.label,
      createTime: dayjs(team.createTime).format('YYYY-MM-DD HH:mm:ss'),
    };
  } catch (error) {}
};

watch(
  [menuStore, userInfoStore],
  ([newMenuStore, newUserInfoStore]) => {
    menuItems.value = _.cloneDeep(newMenuStore.menuList);
    userInfo.value = _.cloneDeep(newUserInfoStore.userDetail);
    if (!userInfo.value.id) return;
    getTeamOptions();
  },
  {
    immediate: true,
    deep: true,
  },
);

const handleTeamClick = () => {
  showTeam.value = !showTeam.value;
};

// 获取用户信息
const getUserInfo = async () => {
  try {
    const res: any = await getUserDetail({ id: userInfo.value.id });
    userInfoStore.setUserDetail(res);
  } catch (error) {}
};

// 格式化菜单列表
const formatMenuList = async (list: any) => {
  const menuList = list.map((item: any) => {
    let menu = {
      key: item.data.keyValue,
      name: item.data.authName,
      path: item.data.path,
      icon: item.data.icon,
      sort: item.data.sortNo,
      tabs: item.childrenNodes.map((tab: any) => {
        return {
          key: tab.data.keyValue,
          name: tab.data.authName,
        };
      }),
    };
    return menu;
  });
  menuList.sort((a: any, b: any) => {
    return a.sort - b.sort;
  });
  menuItems.value = menuList;
  menuStore.setMenuList(menuList);
};

// 切换团队
const handleChooseTeam = async (record) => {
  if (record.disabled) return;
  if (currentTeam.value.id === record.value) return;
  loading.value = true;
  const team: any = teamOptions.value.find((item: any) => item.value === record.value);
  currentTeam.value = {
    id: team.value,
    name: team.label,
    createTime: dayjs(team.createTime).format('YYYY-MM-DD HH:mm:ss'),
  };
  teamNameOpacity.value = 1;
  try {
    const res: any = await getPermissionById({ id: team.value });
    await switchOrganization({
      organizationId: team.value,
      userId: userInfo.value.id,
    });
    await formatMenuList(res);
    await getUserInfo();
    showTeam.value = false;
    notification.success({
      message: '切换团队成功',
      description: `团队已切换至${team.label}`,
    });
  } catch (e: any) {
    notification.error({
      message: '切换团队失败',
      description: e,
    });
  } finally {
    setTimeout(() => {
      loading.value = false;
      teamNameOpacity.value = 0;
    }, 1500);
  }
};

const handleClick = (item) => {
  activeKey.value = [item.key];
  if (item.tabs && item.tabs.length) {
    router.push({ path: `${item.path}/${item.tabs[0].key}` });
  } else {
    router.push({ path: item.path });
  }
};
</script>
<style lang="scss" scoped>
.sidebar {
  width: 13rem;
  transition: width 0.3s ease-in-out;
}

.collapsed {
  width: 82px;
  .sidebar_text {
    opacity: 0;
    display: none;
    pointer-events: none;
  }
  .height_animate {
    height: 58px;
    width: 58px;
    border-radius: 50%;
  }
}

.sidebar_text {
  opacity: 1;
  transition: opacity 0.3s ease;
}

.height_animate {
  height: auto;
  transition: height 0.3s ease;
}

.body_bg {
  padding: 0 2rem 1.25rem 2rem;
  background: transparent;
  overflow-y: hidden;
}

.team_item {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0px 0px 40px 10px rgba(0, 0, 0, 0.03);
  &:hover {
    border: 2px solid #d9b3ff;
  }
  &.active {
    border: 2px solid #d9b3ff;
  }
}

.team_active {
  box-shadow: 0px 0px 15px 0px rgba(0, 0, 0, 0.05);
}

.team_disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 自定义工具类 */
.team-loader {
  position: relative;
  overflow: hidden;
}
.team-loader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  --tw-gradient-from: transparent var(--tw-gradient-from-position);
  --tw-gradient-stops:
    var(--tw-gradient-from), rgb(22 93 255 / 0.1) var(--tw-gradient-via-position), var(--tw-gradient-to);
  --tw-gradient-to: transparent var(--tw-gradient-to-position);
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
  transition-duration: 500ms;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  animation: shine 1.5s infinite linear;
}
.dot-pulse {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: #165dff;
  margin: 0 0.25rem;
  animation: pulse 1.4s infinite ease-in-out both;
}
.dot-pulse:nth-child(2) {
  animation-delay: 0.2s;
}
.dot-pulse:nth-child(3) {
  animation-delay: 0.4s;
}
.team-name-fade {
  animation: fade 2s infinite ease-in-out alternate;
}

/* 关键帧动画 */
@keyframes shine {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
}
@keyframes fade {
  0% {
    opacity: 0.6;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

/* 自定义颜色变量（对应 Tailwind 配置） */
:root {
  --color-primary: #165dff;
  --color-secondary: #4080ff;
  --color-neutral: #f5f7fa;
}

/* 映射 Tailwind 颜色类 */
.bg-primary\/10 {
  background-color: rgba(22, 93, 255, 0.1);
}
.text-primary {
  color: var(--color-primary);
}
.bg-neutral {
  background-color: var(--color-neutral);
}

/* 页面切换动画 */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
</style>
